import React from "react";
import {
    Document, Page, Text, View,
    StyleSheet, pdf,
} from "@react-pdf/renderer";

const BORDER     = "0.5pt solid #000";
const FONT_SIZE  = 7; 
const HEADER_BG  = "#BDD7EE";

const styles = StyleSheet.create({
    page: { padding: 30, fontSize: FONT_SIZE, fontFamily: "Helvetica", backgroundColor: "#fff" },
    headerRow:       { flexDirection: "row", alignItems: "center", marginBottom: 15 },
    headerTextBlock: { flex: 1, alignItems: "center" },
    headerTitle:     { fontSize: 10, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 },
    headerSubtitle:  { fontSize: 8, textAlign: "center", marginBottom: 2 },
    
    infoBox:         { marginTop: 6, alignItems: "center", borderTop: "0.5pt solid #000", paddingTop: 5, width: "100%" },
    levelText:       { fontSize: 9, fontFamily: "Helvetica-Bold" },
    examTitle:       { fontSize: 9, fontFamily: "Helvetica-Bold", textDecoration: "underline", marginTop: 2 },

    table:           { width: "100%", borderLeft: BORDER, borderTop: BORDER, marginTop: 10 },
    row:             { flexDirection: "row", minHeight: 18, alignItems: 'stretch' },
    cell: { 
        borderRight: BORDER, 
        borderBottom: BORDER, 
        justifyContent: "center", 
        paddingHorizontal: 5, 
        paddingVertical: 2 
    },
    cellText:     { fontSize: FONT_SIZE, textAlign: "center" },
    cellTextLeft: { fontSize: FONT_SIZE, textAlign: "left" },
    cellTextBold: { fontFamily: "Helvetica-Bold" },
    rowEven:      { backgroundColor: "#f2f2f2" },
    
    footer: { marginTop: 20, flexDirection: "row", justifyContent: "flex-end" },
    footerText: { fontSize: 8, fontFamily: "Helvetica-Bold" },
});

/**
 * CONFIGURATION DES LARGEURS (TOTAL 100%)
 * Colonne Moyenne supprimée, espace redistribué sur le Nom/Prénom et Décision
 */
const COL_W = {
    INDEX: "3%",
    MATRICULE: "13%",
    NOM: "19%",
    PRENOM: "19%",
    NAISSANCE: "11%",
    SEXE: "4%",
    CREDITS: "6%",
    DECISION: "25%"
};

// Fonction de traduction des statuts
const translateStatus = (status) => {
    switch (status) {
        case "ADMITTED":
            return "ADMISSIBLE";
        case "RETAKE":
            return "AJOURNÉ(E)";
        case "ADMITTED_WITH_DEBT":
            return "ADMISSIBLE (AVEC CREDIT)";
        default:
            return status || "-";
    }
};

const Cell = ({ width, style, children, textStyle, isLeft = false }) => (
    <View style={[styles.cell, { width }, style]}>
        <Text style={[isLeft ? styles.cellTextLeft : styles.cellText, textStyle]} numberOfLines={1}>
            {children ?? ""}
        </Text>
    </View>
);

const FinalDecisionPDFDocument = ({ results = [], sessionInfo = null, studyLevelName = "" }) => {
    const academicYear = sessionInfo?.academic_year || "Non renseignée";
    const sessionLabel = sessionInfo?.label || sessionInfo?.title || "N/A";

    return (
        <Document title={`PV_DELIBERATION_${sessionLabel}`}>
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* Section En-tête */}
                <View style={styles.headerRow}>
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerTitle}>UNIVERSITE DES SCIENCES, DES TECHNIQUES ET DES TECHNOLOGIES DE BAMAKO (USTTB)</Text>
                        <Text style={styles.headerTitle}>FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE (FMOS)</Text>
                        <Text style={styles.headerSubtitle}>SESSION : {sessionLabel.toUpperCase()} — ANNÉE UNIVERSITAIRE : {academicYear}</Text>
                        
                        <View style={styles.infoBox}>
                            <Text style={styles.levelText}>PROCÈS-VERBAL DE DÉLIBÉRATION FINALE</Text>
                            <Text style={styles.examTitle}>NIVEAU : {(studyLevelName || "N/A").toUpperCase()}</Text>
                        </View>
                    </View>
                </View>

                {/* Section Tableau */}
                <View style={styles.table}>
                    <View style={[styles.row, { backgroundColor: HEADER_BG }]}>
                        <Cell width={COL_W.INDEX} textStyle={styles.cellTextBold}>#</Cell>
                        <Cell width={COL_W.MATRICULE} textStyle={styles.cellTextBold}>Matricule</Cell>
                        <Cell width={COL_W.NOM} textStyle={styles.cellTextBold}>Nom</Cell>
                        <Cell width={COL_W.PRENOM} textStyle={styles.cellTextBold}>Prénom(s)</Cell>
                        <Cell width={COL_W.NAISSANCE} textStyle={styles.cellTextBold}>Né(e) le</Cell>
                        <Cell width={COL_W.SEXE} textStyle={styles.cellTextBold}>Sexe</Cell>
                        <Cell width={COL_W.CREDITS} textStyle={styles.cellTextBold}>Créd.</Cell>
                        <Cell width={COL_W.DECISION} textStyle={styles.cellTextBold}>Décision</Cell>
                    </View>

                    {results.map((r, i) => (
                        <View key={i} style={[styles.row, i % 2 !== 0 ? styles.rowEven : {}]} wrap={false}>
                            <Cell width={COL_W.INDEX}>{i + 1}</Cell>
                            <Cell width={COL_W.MATRICULE} textStyle={styles.cellTextBold}>{r.student_number}</Cell>
                            <Cell width={COL_W.NOM} isLeft={true} textStyle={styles.cellTextBold}>{r.last_name?.toUpperCase()}</Cell>
                            <Cell width={COL_W.PRENOM} isLeft={true}>{r.first_name}</Cell>
                            <Cell width={COL_W.NAISSANCE}>{r.birth_date || "-"}</Cell>
                            <Cell width={COL_W.SEXE}>{r.sex}</Cell>
                            <Cell width={COL_W.CREDITS}>{r.total_credits_validated}</Cell>
                            
                            <Cell width={COL_W.DECISION} textStyle={styles.cellTextBold}>
                                {translateStatus(r.final_status)}
                            </Cell>
                        </View>
                    ))}
                </View>

                {/* Pied de page */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Fait à Bamako, le {new Date().toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                        })}
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export async function generateFinalDecisionPDF({ results, sessionInfo, studyLevelName }) {
    const blob = await pdf(
        <FinalDecisionPDFDocument 
            results={results} 
            sessionInfo={sessionInfo} 
            studyLevelName={studyLevelName} 
        />
    ).toBlob();

    const filename = `PV_DELIBERATION_${(studyLevelName || "SESSION").replace(/\s+/g, "_")}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}